import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { REQUIRED_RED } from '../../App';
import { MuiTextFieldWithFocus } from '../../config/MaterialUIConstants';
import { addTermsAndWarrenty, deleteTermsAndWarrenty, updateTermsAndWarrenty } from '../../redux/Account/ShopDetails/action';
import { coreSettingsKeys } from '../../services/core/settings';
import CommonDialog from '../CommonDialog';

export const termsModalPaperSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: '12px',
    boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
    width: '560px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const termsFieldLabelSx = {
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 'normal',
    mb: '8px',
};

const normalizeSavedItem = (result, formValues, editId) => {
    const id = result?._id || editId || null;
    const title = result?.title ?? formValues?.title ?? '';
    const description = result?.description ?? formValues?.description ?? '';
    const isInvoiceDefault = Boolean(result?.isInvoiceDefault ?? result?.isDefaultInvoice ?? formValues?.isInvoiceDefault);
    const isEstimateDefault = Boolean(result?.isEstimateDefault ?? result?.isDefaultEstimate ?? formValues?.isEstimateDefault);
    return { _id: id, title, description, isInvoiceDefault, isEstimateDefault };
};

const normalizeDefaultFlags = (item = {}) => ({
    isInvoiceDefault: Boolean(item?.isInvoiceDefault ?? item?.isDefaultInvoice),
    isEstimateDefault: Boolean(item?.isEstimateDefault ?? item?.isDefaultEstimate),
});

/**
 * Shared add/edit Terms & Warranty modal (shop, invoice, estimate).
 */
const TermsAndWarrantyModal = ({
    open,
    onClose,
    mode = 'add',
    initialValues = { title: '', description: '' },
    onSuccess,
    onDeleted,
    showDelete = true,
    onRefreshShopDetails,
    disableBackdropClose = true,
    showInvoiceDefaultCheckbox = false,
    showEstimateDefaultCheckbox = false,
}) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const isEditMode = mode === 'edit';
    const editId = initialValues?._id || null;

    const [showLoader, setShowLoader] = useState(false);
    const [allowApiCall, setAllowApiCall] = useState(true);
    const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const showDefaultCheckboxes = showInvoiceDefaultCheckbox || showEstimateDefaultCheckbox;

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: { title: '', description: '', isInvoiceDefault: false, isEstimateDefault: false },
    });

    useEffect(() => {
        if (open) {
            const defaultFlags = normalizeDefaultFlags(initialValues);
            reset({
                title: initialValues?.title || '',
                description: initialValues?.description || '',
                isInvoiceDefault: defaultFlags.isInvoiceDefault,
                isEstimateDefault: defaultFlags.isEstimateDefault,
            });
            setAllowApiCall(true);
        }
    }, [open, initialValues, reset]);

    const invalidateTerms = () => {
        queryClient.invalidateQueries({
            queryKey: coreSettingsKeys.termsAndWarranty(),
            refetchType: 'all',
        });
    };

    const handleClose = () => {
        if (showLoader || deleteLoader) return;
        onClose?.();
    };

    const handleSubmitTerms = async (values) => {
        if (!allowApiCall) return;

        setAllowApiCall(false);
        setShowLoader(true);

        const payload = {
            title: values.title,
            description: values.description,
            isInvoiceDefault: values.isInvoiceDefault || false,
            isEstimateDefault: values.isEstimateDefault || false,
        };

        let result;
        if (editId) {
            result = await dispatch(updateTermsAndWarrenty(payload, editId));
        } else {
            result = await dispatch(addTermsAndWarrenty(payload));
        }

        if (result) {
            invalidateTerms();
            onRefreshShopDetails?.();
            const saved = normalizeSavedItem(result, values, editId);
            onSuccess?.(saved);
            handleClose();
        }

        setShowLoader(false);
        setAllowApiCall(true);
    };

    const handleDelete = async () => {
        if (!editId) return;
        setDeleteLoader(true);
        const result = await dispatch(deleteTermsAndWarrenty(editId));
        if (result) {
            invalidateTerms();
            onRefreshShopDetails?.();
            setOpenDeleteConfirmModal(false);
            onDeleted?.(editId);
            handleClose();
        }
        setDeleteLoader(false);
    };

    return (
        <>
            <Modal
                open={open}
                onClose={(event, reason) => {
                    if (disableBackdropClose && reason === 'backdropClick') return;
                    handleClose();
                }}
                aria-labelledby="terms-modal-title"
                aria-describedby="terms-modal-description"
            >
                <Box sx={termsModalPaperSx}>
                    <Box
                        sx={{
                            background: '#FFF',
                            boxShadow: 'none',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.10)!important',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            minHeight: '62px',
                            px: '24px',
                        }}
                    >
                        <Typography id="terms-modal-title" variant="h6" color="#1F1F1F" fontSize="20px" fontWeight={500} lineHeight="32px">
                            {isEditMode ? 'Edit Terms or Warranty' : 'Add Terms or Warranty'}
                        </Typography>
                        <IconButton
                            onClick={handleClose}
                            aria-label="Close modal"
                            disabled={showLoader}
                            sx={{ color: '#1F1F1F', width: 36, height: 36, mr: '-8px' }}
                        >
                            <CloseIcon sx={{ fontSize: 32 }} />
                        </IconButton>
                    </Box>

                    <form
                        onSubmit={handleSubmit(handleSubmitTerms)}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                        }}
                    >
                        <Box
                            sx={{
                                px: '24px',
                                pt: '24px',
                                pb: '22px',
                                flex: 1,
                                overflowY: 'auto',
                                minHeight: 0,
                            }}
                        >
                            <Grid container columnSpacing={3} rowSpacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="body1" sx={termsFieldLabelSx}>
                                        Title <span style={{ color: REQUIRED_RED }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="title"
                                        control={control}
                                        rules={{ required: 'Title is required' }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                id="terms-title"
                                                variant="outlined"
                                                fullWidth
                                                value={field.value || ''}
                                                onChange={(e) => {
                                                    setAllowApiCall(true);
                                                    field.onChange(e);
                                                }}
                                                error={!!errors.title}
                                                helperText={errors.title ? errors.title.message : ''}
                                                InputLabelProps={{ shrink: !!field.value?.length }}
                                                sx={MuiTextFieldWithFocus}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body1" sx={termsFieldLabelSx}>
                                        Description <span style={{ color: REQUIRED_RED }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="description"
                                        control={control}
                                        rules={{ required: 'Description is required' }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                variant="outlined"
                                                fullWidth
                                                multiline
                                                minRows={4}
                                                value={field.value || ''}
                                                onChange={(e) => {
                                                    setAllowApiCall(true);
                                                    field.onChange(e);
                                                }}
                                                error={!!errors.description}
                                                helperText={errors.description ? errors.description.message : ''}
                                                sx={{
                                                    '& .MuiInputBase-input::placeholder': {
                                                        fontWeight: 400,
                                                        lineHeight: '130%',
                                                        letterSpacing: '0.15px',
                                                        color: '#9E9E9E',
                                                        opacity: 1,
                                                    },
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        padding: '12px!important',
                                                        boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                                        '& fieldset': {
                                                            border: '1px solid rgba(0, 0, 0, 0.10)',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                {showDefaultCheckboxes && (
                                    <Grid item xs={12} sx={{ mt: '-8px', ml: '10px' }}>
                                        <Stack
                                            direction="row"
                                            sx={{
                                                gap: '10px',
                                            }}
                                        >
                                            <Controller
                                                name="isInvoiceDefault"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                {...field}
                                                                checked={Boolean(field.value)}
                                                                onChange={(event) => {
                                                                    setAllowApiCall(true);
                                                                    field.onChange(event.target.checked);
                                                                }}
                                                                sx={{
                                                                    p: 0,
                                                                    mr: 1,

                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: 20,
                                                                    },

                                                                    color: '#BDBDBD',

                                                                    '&.Mui-checked': {
                                                                        color: '#006CDD',
                                                                    },
                                                                }}
                                                                icon={
                                                                    <span
                                                                        style={{
                                                                            width: 20,
                                                                            height: 20,
                                                                            borderRadius: '4px',
                                                                            border: '2px solid #E6E6E6',
                                                                            backgroundColor: '#fff',
                                                                            display: 'inline-block',
                                                                            boxSizing: 'border-box',
                                                                            boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                                                        }}
                                                                    />
                                                                }
                                                            />
                                                        }
                                                        label="Set as default for Invoices"
                                                        sx={{
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '16px',
                                                                fontWeight: 400,
                                                                color: '#212121',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                name="isEstimateDefault"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                {...field}
                                                                checked={Boolean(field.value)}
                                                                onChange={(event) => {
                                                                    setAllowApiCall(true);
                                                                    field.onChange(event.target.checked);
                                                                }}
                                                                sx={{
                                                                    p: 0,
                                                                    mr: 1,

                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: 20,
                                                                    },

                                                                    color: '#BDBDBD',

                                                                    '&.Mui-checked': {
                                                                        color: '#006CDD',
                                                                    },
                                                                }}
                                                                icon={
                                                                    <span
                                                                        style={{
                                                                            width: 20,
                                                                            height: 20,
                                                                            borderRadius: '4px',
                                                                            border: '2px solid #E6E6E6',
                                                                            backgroundColor: '#fff',
                                                                            display: 'inline-block',
                                                                            boxSizing: 'border-box',
                                                                            boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                                                        }}
                                                                    />
                                                                }
                                                            />
                                                        }
                                                        label="Set as default for Estimates"
                                                        sx={{
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '16px',
                                                                fontWeight: 400,
                                                                color: '#212121',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }} />

                        <Box
                            sx={{
                                px: '16px',
                                py: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Box>
                                {isEditMode && showDelete && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        disabled={showLoader}
                                        sx={{ minWidth: 103, height: 42 }}
                                        onClick={() => {
                                            setOpenDeleteConfirmModal(true);
                                        }}
                                    >
                                        DELETE
                                    </Button>
                                )}
                            </Box>
                            <Stack spacing={2} direction="row">
                                <Button variant="outlined" color="primary" disabled={showLoader} onClick={handleClose} sx={{ minWidth: 103, height: 42 }}>
                                    CANCEL
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={!allowApiCall || showLoader}
                                    sx={{ minWidth: 82, height: 42 }}
                                >
                                    {showLoader ? <CircularProgress size={24} sx={{ color: 'white' }} /> : isEditMode ? 'UPDATE' : 'SAVE'}
                                </Button>
                            </Stack>
                        </Box>
                    </form>
                </Box>
            </Modal>

            <CommonDialog
                loader={deleteLoader}
                open={openDeleteConfirmModal}
                onClose={() => {
                    if (!deleteLoader) setOpenDeleteConfirmModal(false);
                }}
                onSave={handleDelete}
                dialogTitle="Delete Confirmation"
                dialogSubtitle="Are you sure you want to delete this term and warranty?"
            />
        </>
    );
};

export default TermsAndWarrantyModal;
